"use client";
import { jsx } from "react/jsx-runtime";
import {
  editDictionaryByKeyPath,
  getContentNodeByKeyPath,
  renameContentNodeByKeyPath
} from "@intlayer/core";
import { MessageKey } from "@intlayer/editor";
import {
  createContext,
  useContext
} from "react";
import {
  useDictionariesRecord
} from "./DictionariesRecordContext.mjs";
import { useCrossFrameMessageListener } from "./useCrossFrameMessageListener.mjs";
import { useCrossFrameState } from "./useCrossFrameState.mjs";
const EditedContentStateContext = createContext(void 0);
const usePostEditedContentState = (onEventTriggered) => useCrossFrameMessageListener(
  `${MessageKey.INTLAYER_EDITED_CONTENT_CHANGED}/post`,
  onEventTriggered
);
const useGetEditedContentState = (onEventTriggered) => useCrossFrameMessageListener(
  `${MessageKey.INTLAYER_EDITED_CONTENT_CHANGED}/get`,
  onEventTriggered
);
const EditedContentActionsContext = createContext(void 0);
const resolveState = (state, prevState) => typeof state === "function" ? state(prevState) : state;
const EditedContentProvider = ({ children }) => {
  const { localeDictionaries } = useDictionariesRecord();
  const [editedContent, setEditedContentState] = useCrossFrameState(
    MessageKey.INTLAYER_EDITED_CONTENT_CHANGED
  );
  const setEditedDictionary = (newValue) => {
    let updatedDictionaries = resolveState(newValue);
    setEditedContentState((prev) => {
      updatedDictionaries = resolveState(
        newValue,
        prev?.[updatedDictionaries.key]
      );
      return {
        ...prev,
        [updatedDictionaries.key]: updatedDictionaries
      };
    });
    return updatedDictionaries;
  };
  const setEditedContent = (localDictionaryId, newValue) => {
    setEditedContentState((prev) => ({
      ...prev,
      [localDictionaryId]: {
        ...prev?.[localDictionaryId],
        content: newValue
      }
    }));
  };
  const addEditedContent = (localDictionaryId, newValue, keyPath = [], overwrite = true) => {
    setEditedContentState((prev) => {
      const originalContent = localeDictionaries[localDictionaryId]?.content;
      const currentContent = structuredClone(
        prev?.[localDictionaryId]?.content ?? originalContent
      );
      let newKeyPath = keyPath;
      if (!overwrite) {
        let index = 0;
        const otherKeyPath = keyPath.slice(0, -1);
        const lastKeyPath = keyPath[keyPath.length - 1];
        let finalKey = lastKeyPath.key;
        while (typeof getContentNodeByKeyPath(currentContent, newKeyPath) !== "undefined") {
          index++;
          finalKey = index === 0 ? lastKeyPath.key : `${lastKeyPath.key} (${index})`;
          newKeyPath = [
            ...otherKeyPath,
            { ...lastKeyPath, key: finalKey }
          ];
        }
      }
      const updatedContent = editDictionaryByKeyPath(
        currentContent,
        newKeyPath,
        newValue
      );
      return {
        ...prev,
        [localDictionaryId]: {
          ...prev?.[localDictionaryId],
          content: updatedContent
        }
      };
    });
  };
  const renameEditedContent = (localDictionaryId, newKey, keyPath = []) => {
    setEditedContentState((prev) => {
      const originalContent = localeDictionaries[localDictionaryId]?.content;
      const currentContent = structuredClone(
        prev?.[localDictionaryId]?.content ?? originalContent
      );
      const contentWithNewField = renameContentNodeByKeyPath(
        currentContent,
        newKey,
        keyPath
      );
      return {
        ...prev,
        [localDictionaryId]: {
          ...prev?.[localDictionaryId],
          content: contentWithNewField
        }
      };
    });
  };
  const removeEditedContent = (localDictionaryId, keyPath) => {
    setEditedContentState((prev) => {
      const originalContent = localeDictionaries[localDictionaryId]?.content;
      const currentContent = structuredClone(
        prev?.[localDictionaryId]?.content ?? originalContent
      );
      const initialContent = getContentNodeByKeyPath(originalContent, keyPath);
      const restoredContent = editDictionaryByKeyPath(
        currentContent,
        keyPath,
        initialContent
      );
      return {
        ...prev,
        [localDictionaryId]: {
          ...prev?.[localDictionaryId],
          content: restoredContent
        }
      };
    });
  };
  const restoreEditedContent = (localDictionaryId) => {
    setEditedContentState((prev) => {
      const updated = { ...prev };
      delete updated[localDictionaryId];
      return updated;
    });
  };
  const clearEditedDictionaryContent = (localDictionaryId) => {
    setEditedContentState((prev) => {
      const filtered = Object.entries(prev).reduce((acc, [key, value]) => {
        if (key === localDictionaryId) {
          return acc;
        }
        return { ...acc, [key]: value };
      }, {});
      return filtered;
    });
  };
  const clearEditedContent = () => {
    setEditedContentState({});
  };
  const getEditedContentValue = (localDictionaryIdOrKey, keyPath) => {
    if (!editedContent) return void 0;
    const isDictionaryId = localDictionaryIdOrKey.includes(":local:") || localDictionaryIdOrKey.includes(":remote:");
    if (isDictionaryId) {
      const currentContent = editedContent?.[localDictionaryIdOrKey]?.content ?? {};
      const contentNode = getContentNodeByKeyPath(currentContent, keyPath);
      return contentNode;
    }
    const filteredDictionariesLocalId = Object.keys(editedContent).filter(
      (key) => key.startsWith(`${localDictionaryIdOrKey}:`)
    );
    for (const localDictionaryId of filteredDictionariesLocalId) {
      const currentContent = editedContent?.[localDictionaryId]?.content ?? {};
      const contentNode = getContentNodeByKeyPath(currentContent, keyPath);
      if (contentNode) return contentNode;
    }
    return void 0;
  };
  return /* @__PURE__ */ jsx(
    EditedContentStateContext.Provider,
    {
      value: {
        editedContent
      },
      children: /* @__PURE__ */ jsx(
        EditedContentActionsContext.Provider,
        {
          value: {
            setEditedContentState,
            setEditedDictionary,
            setEditedContent,
            addEditedContent,
            renameEditedContent,
            removeEditedContent,
            restoreEditedContent,
            clearEditedDictionaryContent,
            clearEditedContent,
            getEditedContentValue
          },
          children
        }
      )
    }
  );
};
const useEditedContentActions = () => useContext(EditedContentActionsContext);
const useEditedContent = () => {
  const stateContext = useContext(EditedContentStateContext);
  const actionContext = useEditedContentActions();
  return { ...stateContext, ...actionContext };
};
export {
  EditedContentProvider,
  useEditedContent,
  useEditedContentActions,
  useGetEditedContentState,
  usePostEditedContentState
};
//# sourceMappingURL=EditedContentContext.mjs.map